import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  setDoc,
  serverTimestamp,
  arrayUnion,
  writeBatch
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { Customer, Shipment, Invoice, User, ShipmentForm as ShipmentFormType } from '../types'
import { storage } from '../config/firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

const generateTrackingNumber = () => {
  const prefix = 'SHP';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

// Customer Services
export const customerService = {
  // Get all customers (active only by default)
  async getAllCustomers(includeInactive: boolean = false): Promise<Customer[]> {
    try {
      const customersRef = collection(db, 'customers')
      const q = query(customersRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      
      let customers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Customer[]
      
      // Filter out inactive customers unless specifically requested
      if (!includeInactive) {
        customers = customers.filter(customer => customer.isActive !== false)
      }
      
      return customers
    } catch (error) {
      console.error('Error fetching customers:', error)
      throw error
    }
  },

  // Get customer by ID
  async getCustomerById(id: string): Promise<Customer | null> {
    try {
      const customerRef = doc(db, 'customers', id)
      const customerSnap = await getDoc(customerRef)
      
      if (customerSnap.exists()) {
        const data = customerSnap.data()
        return {
          id: customerSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Customer
      }
      return null
    } catch (error) {
      console.error('Error fetching customer:', error)
      throw error
    }
  },

  // Create customer
  async createCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>, userId: string): Promise<Customer> {
    try {
      const customerRef = doc(collection(db, 'customers'))
      const newCustomer: Omit<Customer, 'id'> = {
        ...customerData,
        isActive: true, // Ensure new customers are active
        createdBy: userId,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      }
      
      await setDoc(customerRef, newCustomer)
      
      return {
        id: customerRef.id,
        ...newCustomer,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Customer
    } catch (error) {
      console.error('Error creating customer:', error)
      throw error
    }
  },

  // Update customer
  async updateCustomer(id: string, customerData: Partial<Customer>): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id)
      await updateDoc(customerRef, {
        ...customerData,
        updatedAt: serverTimestamp() as any,
      })
    } catch (error) {
      console.error('Error updating customer:', error)
      throw error
    }
  },

  // Deactivate customer (safer than delete - preserves data)
  async deactivateCustomer(id: string): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id)
      await updateDoc(customerRef, {
        isActive: false,
        deactivatedAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      })
    } catch (error) {
      console.error('Error deactivating customer:', error)
      throw error
    }
  },

  // Reactivate customer
  async reactivateCustomer(id: string): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id)
      await updateDoc(customerRef, {
        isActive: true,
        deactivatedAt: null,
        updatedAt: serverTimestamp() as any,
      })
    } catch (error) {
      console.error('Error reactivating customer:', error)
      throw error
    }
  },

  // Delete customer (use with caution - this permanently removes the customer)
  async deleteCustomer(id: string): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id)
      await deleteDoc(customerRef)
    } catch (error) {
      console.error('Error deleting customer:', error)
      throw error
    }
  }
}

// Shipment Services
export const shipmentService = {
  // Get all shipments
  async getAllShipments(): Promise<Shipment[]> {
    try {
      const shipmentsRef = collection(db, 'shipments')
      const q = query(shipmentsRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Shipment[]
    } catch (error) {
      console.error('Error fetching shipments:', error)
      throw error
    }
  },

  // Get shipments by status
  async getShipmentsByStatus(status: string): Promise<Shipment[]> {
    try {
      const shipmentsRef = collection(db, 'shipments')
      const q = query(
        shipmentsRef, 
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Shipment[]
    } catch (error) {
      console.error('Error fetching shipments by status:', error)
      throw error
    }
  },

  // Get shipments by customer
  async getShipmentsByCustomer(customerId: string): Promise<Shipment[]> {
    try {
      const shipmentsRef = collection(db, 'shipments')
      const q = query(
        shipmentsRef, 
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Shipment[]
    } catch (error) {
      console.error('Error fetching shipments by customer:', error)
      throw error
    }
  },

  // Get shipment by ID
  async getShipmentById(id: string): Promise<Shipment | null> {
    try {
      const shipmentRef = doc(db, 'shipments', id)
      const shipmentSnap = await getDoc(shipmentRef)
      
      if (shipmentSnap.exists()) {
        const data = shipmentSnap.data()
        return {
          id: shipmentSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Shipment
      }
      return null
    } catch (error) {
      console.error('Error fetching shipment:', error)
      throw error
    }
  },

  async createShipment(shipmentData: ShipmentFormType, userId: string): Promise<string> {
    try {
      const customerRef = doc(db, 'customers', shipmentData.customerId);
      const customerSnap = await getDoc(customerRef);
  
      if (!customerSnap.exists()) {
        throw new Error("Customer not found");
      }
  
      const customerData = { id: customerSnap.id, ...customerSnap.data() } as Customer;

      const newShipment = {
        ...shipmentData,
        trackingNumber: generateTrackingNumber(),
        status: 'pending',
        customer: customerData,
        documents: [], // Handle document uploads separately
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        photos: shipmentData.photos || [],
      };
      const docRef = await addDoc(collection(db, 'shipments'), newShipment);
      return docRef.id;
    } catch (error) {
      console.error('Error adding shipment: ', error);
      throw error;
    }
  },

  // Get single shipment
  async getShipment(id: string): Promise<Shipment | null> {
    try {
      const docRef = doc(db, 'shipments', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Manually convert Timestamps to Dates
        if (data.createdAt && data.createdAt.toDate) {
          data.createdAt = data.createdAt.toDate();
        }
        if (data.updatedAt && data.updatedAt.toDate) {
          data.updatedAt = data.updatedAt.toDate();
        }
        if (data.estimatedDeliveryDate && data.estimatedDeliveryDate.toDate) {
          data.estimatedDeliveryDate = data.estimatedDeliveryDate.toDate();
        }
        return { id: docSnap.id, ...data } as Shipment;
      }
      return null;
    } catch (error) {
      console.error('Error fetching shipment:', error);
      throw error;
    }
  },

  // Update a shipment
  async updateShipment(id: string, shipmentData: Partial<Shipment>): Promise<void> {
    try {
      const shipmentRef = doc(db, 'shipments', id)
      await updateDoc(shipmentRef, {
        ...shipmentData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Error updating shipment:', error)
      throw error
    }
  }
}

// Invoice Services
export const invoiceService = {
  // Get all invoices
  async getAllInvoices(): Promise<Invoice[]> {
    try {
      const invoicesRef = collection(db, 'invoices')
      const q = query(invoicesRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate(),
        paidDate: doc.data().paidDate?.toDate(),
      })) as Invoice[]
    } catch (error) {
      console.error('Error fetching invoices:', error)
      throw error
    }
  },

  // Get outstanding invoices
  async getOutstandingInvoices(): Promise<Invoice[]> {
    try {
      const invoicesRef = collection(db, 'invoices')
      const q = query(
        invoicesRef,
        where('status', 'in', ['draft', 'sent', 'overdue']),
        orderBy('dueDate', 'asc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate(),
        paidDate: doc.data().paidDate?.toDate(),
      })) as Invoice[]
    } catch (error) {
      console.error('Error fetching outstanding invoices:', error)
      throw error
    }
  },

  // Get paid invoices
  async getPaidInvoices(): Promise<Invoice[]> {
    try {
      const invoicesRef = collection(db, 'invoices')
      const q = query(
        invoicesRef,
        where('status', '==', 'paid'),
        orderBy('paidDate', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate() || new Date(),
        paidDate: doc.data().paidDate?.toDate() || new Date(),
      })) as Invoice[]
    } catch (error) {
      console.error('Error fetching paid invoices:', error)
      throw error
    }
  },

  // Get invoices by customer
  async getInvoicesByCustomer(customerId: string): Promise<Invoice[]> {
    try {
      const invoicesRef = collection(db, 'invoices')
      const q = query(
        invoicesRef,
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate(),
        paidDate: doc.data(). paidDate?.toDate(),
      })) as Invoice[]
    } catch (error) {
      console.error('Error fetching invoices by customer:', error)
      throw error
    }
  },

  // Create new invoice
  async createInvoice(invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'invoiceNumber' | 'customer' | 'subtotal' | 'total' | 'status'>, userId: string): Promise<Invoice> {
    try {
      const customerRef = doc(db, 'customers', invoiceData.customerId);
      const customerSnap = await getDoc(customerRef);

      if (!customerSnap.exists()) {
        throw new Error("Customer not found");
      }
      const customer = { id: customerSnap.id, ...customerSnap.data() } as Customer;

      const subtotal = invoiceData.items.reduce((acc, item) => acc + item.quantity * item.unitPrice, 0);
      const total = subtotal + (subtotal * (invoiceData.tax || 0) / 100);

      const newInvoice = {
        ...invoiceData,
        invoiceNumber: `INV-${Date.now()}`,
        customer,
        subtotal,
        total,
        status: 'draft',
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const invoicesRef = collection(db, 'invoices')
      const docRef = await addDoc(invoicesRef, newInvoice)
      
      const newInvoiceSnap = await getDoc(docRef);
      return {
        id: newInvoiceSnap.id,
        ...newInvoiceSnap.data(),
      } as Invoice;
    } catch (error) {
      console.error('Error creating invoice:', error)
      throw error
    }
  },

  // Get single invoice
  async getInvoice(id: string): Promise<Invoice | null> {
    try {
      const docRef = doc(db, 'invoices', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        // Convert Firestore timestamp to Date object
        const data = docSnap.data();
        if (data.dueDate && data.dueDate.toDate) {
          data.dueDate = data.dueDate.toDate();
        }
        if (data.createdAt && data.createdAt.toDate) {
          data.createdAt = data.createdAt.toDate();
        }
        return { id: docSnap.id, ...data } as Invoice;
      }
      return null;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  },

  // Update invoice
  async updateInvoice(id: string, invoiceData: Partial<Invoice>): Promise<void> {
    try {
      const invoiceRef = doc(db, 'invoices', id)
      await updateDoc(invoiceRef, {
        ...invoiceData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Error updating invoice:', error)
      throw error
    }
  }
}

// Dashboard Stats
export async function getDashboardStats() {
  try {
    const customersSnapshot = await getDocs(collection(db, 'customers'));
    const shipmentsSnapshot = await getDocs(collection(db, 'shipments'));
    const invoicesSnapshot = await getDocs(collection(db, 'invoices'));

    const shipments = shipmentsSnapshot.docs.map(doc => doc.data() as Shipment);
    const invoices = invoicesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as Invoice[];

    // Revenue calculations
    const outstandingInvoices = invoices.filter(
      invoice => invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue'
    );
    const paidInvoices = invoices.filter(invoice => invoice.status === 'paid');
    const pendingInvoices = invoices.filter(invoice => invoice.status === 'pending');

    const outstandingRevenue = outstandingInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    const totalEarned = paidInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    const pendingRevenue = pendingInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    
    // Monthly revenue breakdown
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlyPaidInvoices = paidInvoices.filter(invoice => {
      const invoiceDate = invoice.paidDate ? new Date(invoice.paidDate) : new Date(invoice.createdAt);
      return invoiceDate.getMonth() === currentMonth && invoiceDate.getFullYear() === currentYear;
    });
    const monthlyRevenue = monthlyPaidInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);

    // Payment method breakdown
    const paymentMethods = {
      cash: paidInvoices.filter(invoice => invoice.paymentData?.paymentMethod === 'cash').length,
      bankTransfer: paidInvoices.filter(invoice => invoice.paymentData?.paymentMethod === 'bank-transfer').length,
      other: paidInvoices.filter(invoice => !invoice.paymentData?.paymentMethod).length
    };

    const shipmentStatusCounts = shipments.reduce((acc, shipment) => {
      const status = shipment.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalCustomers: customersSnapshot.size,
      totalShipments: shipmentsSnapshot.size,
      outstandingInvoices: outstandingInvoices.length,
      outstandingRevenue,
      totalEarned,
      pendingRevenue,
      monthlyRevenue,
      paymentMethods,
      shipmentStatusCounts,
      revenueBreakdown: {
        outstanding: outstandingRevenue,
        earned: totalEarned,
        pending: pendingRevenue,
        monthly: monthlyRevenue
      }
    };
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
}

// Revenue Reset Function for Launch Preparation
export async function resetRevenueData() {
  try {
    console.log('🔄 Starting revenue reset process...');
    
    // Check if we have access to Firestore
    if (!db) {
      throw new Error('Firestore database not initialized');
    }
    
    // Get all invoices
    console.log('📊 Fetching invoices from Firestore...');
    const invoicesSnapshot = await getDocs(collection(db, 'invoices'));
    const invoices = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Invoice[];
    
    console.log(`📊 Found ${invoices.length} total invoices`);
    
    // Filter invoices that need to be reset (paid or pending)
    const invoicesToReset = invoices.filter(invoice => 
      invoice.status === 'paid' || invoice.status === 'pending'
    );
    
    console.log(`📊 Found ${invoicesToReset.length} invoices to reset`);
    
    if (invoicesToReset.length === 0) {
      console.log('✅ No revenue data to reset - already at 0');
      return { success: true, message: 'No revenue data to reset' };
    }
    
    // Try batch operation first
    try {
      console.log('🔄 Attempting batch write...');
      const batch = writeBatch(db);
      let resetCount = 0;
      
      for (const invoice of invoicesToReset) {
        console.log(`🔄 Adding invoice ${invoice.invoiceNumber} to batch`);
        const invoiceRef = doc(db, 'invoices', invoice.id);
        
        // Reset to draft status and clear payment data
        batch.update(invoiceRef, {
          status: 'draft',
          paidDate: null,
          paymentData: null,
          updatedAt: serverTimestamp() as any,
          // Add a note about when it was reset
          resetForLaunch: {
            resetAt: serverTimestamp() as any,
            originalStatus: invoice.status,
            originalPaidDate: invoice.paidDate,
            originalPaymentData: invoice.paymentData
          }
        });
        
        resetCount++;
      }
      
      // Commit the batch
      console.log(`🔄 Committing batch with ${resetCount} updates...`);
      await batch.commit();
      
      console.log(`✅ Successfully reset ${resetCount} invoices using batch operation`);
      
      return {
        success: true,
        message: `Successfully reset ${resetCount} invoices. Revenue is now 0.`,
        resetCount,
        resetInvoices: invoicesToReset.map(inv => ({
          id: inv.id,
          invoiceNumber: inv.invoiceNumber,
          originalStatus: inv.status,
          originalAmount: inv.total
        }))
      };
      
    } catch (batchError) {
      console.warn('⚠️ Batch operation failed, trying individual updates...', batchError);
      
      // Fallback: Update invoices one by one
      console.log('🔄 Falling back to individual invoice updates...');
      let resetCount = 0;
      const errors: string[] = [];
      
      for (const invoice of invoicesToReset) {
        try {
          console.log(`🔄 Updating invoice ${invoice.invoiceNumber} individually...`);
          const invoiceRef = doc(db, 'invoices', invoice.id);
          
          await updateDoc(invoiceRef, {
            status: 'draft',
            paidDate: null,
            paymentData: null,
            updatedAt: serverTimestamp() as any,
            resetForLaunch: {
              resetAt: serverTimestamp() as any,
              originalStatus: invoice.status,
              originalPaidDate: invoice.paidDate,
              originalPaymentData: invoice.paymentData
            }
          });
          
          resetCount++;
          console.log(`✅ Invoice ${invoice.invoiceNumber} updated successfully`);
          
        } catch (individualError) {
          const errorMsg = `Failed to update invoice ${invoice.invoiceNumber}: ${individualError}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }
      
      if (resetCount > 0) {
        console.log(`✅ Successfully reset ${resetCount} invoices using individual updates`);
        
        return {
          success: true,
          message: `Successfully reset ${resetCount} invoices (${errors.length} failed). Revenue is now 0.`,
          resetCount,
          failedCount: errors.length,
          errors,
          resetInvoices: invoicesToReset.slice(0, resetCount).map(inv => ({
            id: inv.id,
            invoiceNumber: inv.invoiceNumber,
            originalStatus: inv.status,
            originalAmount: inv.total
          }))
        };
      } else {
        throw new Error(`All individual updates failed: ${errors.join('; ')}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Error resetting revenue data:', error);
    
    // Provide more specific error information
    let errorMessage = 'Failed to reset revenue data';
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = (error as any).message;
    }
    
    console.error('❌ Detailed error:', {
      error,
      errorType: typeof error,
      errorMessage,
      errorStack: error instanceof Error ? error.stack : 'No stack trace'
    });
    
    throw new Error(`Failed to reset revenue data: ${errorMessage}`);
  }
}

// Function to restore revenue data if needed
export async function restoreRevenueData() {
  try {
    console.log('🔄 Starting revenue restoration process...');
    
    // Get all invoices with reset data
    const invoicesSnapshot = await getDocs(collection(db, 'invoices'));
    const invoices = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Invoice[];
    
    // Filter invoices that were reset for launch
    const invoicesToRestore = invoices.filter(invoice => 
      invoice.resetForLaunch && invoice.status === 'draft'
    );
    
    console.log(`📊 Found ${invoicesToRestore.length} invoices to restore`);
    
    if (invoicesToRestore.length === 0) {
      console.log('✅ No invoices to restore');
      return { success: true, message: 'No invoices to restore' };
    }
    
    // Restore each invoice
    const batch = writeBatch(db);
    let restoreCount = 0;
    
    for (const invoice of invoicesToRestore) {
      if (!invoice.resetForLaunch) continue; // Skip if no reset data
      
      const invoiceRef = doc(db, 'invoices', invoice.id);
      
      // Restore original data
      batch.update(invoiceRef, {
        status: invoice.resetForLaunch.originalStatus,
        paidDate: invoice.resetForLaunch.originalPaidDate,
        paymentData: invoice.resetForLaunch.originalPaymentData,
        updatedAt: serverTimestamp() as any,
        // Remove reset data
        resetForLaunch: null
      });
      
      restoreCount++;
    }
    
    // Commit the batch
    await batch.commit();
    
    console.log(`✅ Successfully restored ${restoreCount} invoices`);
    
    return {
      success: true,
      message: `Successfully restored ${restoreCount} invoices.`,
      restoreCount
    };
    
  } catch (error) {
    console.error('❌ Error restoring revenue data:', error);
    throw new Error(`Failed to restore revenue data: ${error}`);
  }
}

export async function uploadFileToStorage(file: File, path: string): Promise<string> {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file: ", error);
    throw error;
  }
}

const usersCollection = collection(db, 'users');

export const userService = {
  async getAllUsers(): Promise<User[]> {
    const snapshot = await getDocs(usersCollection);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
  },

  async getUserById(uid: string): Promise<User | null> {
    const docRef = doc(usersCollection, uid);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as User : null;
  },

  async createUser(user: User): Promise<void> {
    await setDoc(doc(usersCollection, user.id), user);
  },

  async updateUser(uid: string, data: Partial<User>): Promise<void> {
    await updateDoc(doc(usersCollection, uid), data);
  },

  async deleteUser(uid: string): Promise<void> {
    await deleteDoc(doc(usersCollection, uid));
  },
};

// Function to get shipments for a logged-in customer (using their userId)
export async function getShipmentsForCustomer(userId: string, limitCount?: number): Promise<Shipment[]> {
  try {
    const shipmentsRef = collection(db, 'shipments')
    let q = query(
      shipmentsRef,
      where('customerId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    if (limitCount) {
      q = query(q, limit(limitCount))
    }
    
    const querySnapshot = await getDocs(q)
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Shipment[]
  } catch (error) {
    console.error('Error fetching customer shipments:', error)
    throw error
  }
}

// Function to get invoices for a logged-in customer (using their userId)
export async function getInvoicesForCustomer(userId: string): Promise<Invoice[]> {
  try {
    const invoicesRef = collection(db, 'invoices');
    // Query by customerId field which stores the customer's UID
    const q = query(
      invoicesRef,
      where('customerId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      dueDate: doc.data().dueDate?.toDate() || new Date(),
    })) as Invoice[];
  } catch (error) {
    console.error('Error fetching invoices for customer:', error);
    throw error;
  }
}

export async function uploadImageForShipment(shipmentId: string, file: File): Promise<string> {
  if (!shipmentId) throw new Error("Shipment ID is required.");
  if (!file) throw new Error("File is required.");

  try {
    // 1. Upload file to Firebase Storage
    const filePath = `shipment-images/${shipmentId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    // 2. Update the shipment document in Firestore
    const shipmentRef = doc(db, 'shipments', shipmentId);
    const shipmentSnap = await getDoc(shipmentRef);

    if (!shipmentSnap.exists()) {
      throw new Error("Shipment not found.");
    }
    
    const shipmentData = shipmentSnap.data();
    const existingImages = shipmentData.images || [];

    await updateDoc(shipmentRef, {
      images: [...existingImages, downloadURL],
      updatedAt: serverTimestamp(),
    });

    // Optional: Add to shipment history
    const historyUpdate = {
      date: serverTimestamp(),
      status: `Image uploaded by customer`,
      location: 'Online Portal'
    };
    await updateDoc(shipmentRef, {
      history: arrayUnion(historyUpdate)
    });

    return downloadURL;
  } catch (error) {
    console.error("Error uploading image for shipment:", error);
    throw error;
  }
}

export async function getRecentCustomers(count: number = 6): Promise<Customer[]> {
  try {
    const customersRef = collection(db, 'customers');
    const q = query(customersRef, orderBy('createdAt', 'desc'), limit(count));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Customer[];
  } catch (error) {
    console.error("Error fetching recent customers:", error);
    throw error;
  }
}

export async function getRecentShipments(count: number = 5): Promise<Shipment[]> {
  try {
    const shipmentsRef = collection(db, 'shipments');
    const q = query(shipmentsRef, orderBy('createdAt', 'desc'), limit(count));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as Shipment[];
  } catch (error) {
    console.error("Error fetching recent shipments:", error);
    throw error;
  }
}

export async function updateInvoiceStatus(invoiceId: string, status: string, paidDate?: Date, paymentData?: any): Promise<void> {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId)
    const updateData: any = {
      status,
      updatedAt: serverTimestamp(),
    }
    
    if (status === 'paid' && paidDate) {
      updateData.paidDate = paidDate
    }
    
    if (paymentData) {
      updateData.paymentData = paymentData
    }
    
    await updateDoc(invoiceRef, updateData)
  } catch (error) {
    console.error('Error updating invoice status:', error)
    throw error
  }
}

export async function getCustomerStats(userId: string) {
  try {
    const shipmentsRef = collection(db, 'shipments')
    const q = query(shipmentsRef, where('customerId', '==', userId))
    const querySnapshot = await getDocs(q)
    
    const shipments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Shipment[]
    
    const invoicesRef = collection(db, 'invoices')
    const invoicesQuery = query(invoicesRef, where('customerId', '==', userId))
    const invoicesSnapshot = await getDocs(invoicesQuery)
    
    const invoices = invoicesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Invoice[]
    
    return {
      totalShipments: shipments.length,
      activeShipments: shipments.filter(s => s.status === 'pending' || s.status === 'in_transit').length,
      deliveredShipments: shipments.filter(s => s.status === 'delivered').length,
      totalInvoices: invoices.length,
    }
  } catch (error) {
    console.error('Error fetching customer stats:', error)
    throw error
  }
} 